import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { CreateAccountModal } from '@/components/CreateAccountModal';
import { CreateContactModal } from '@/components/CreateContactModal';

export interface Account {
  id: string;
  parentId: string | null;
  name: string;
  industry: string | null;
  sizeBucket: string | null;
  website: string | null;
  status: string;
}

export function AccountsPage() {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [contactForAccount, setContactForAccount] = useState<string | null>(
    null,
  );

  const load = useCallback(() => {
    setLoading(true);
    apiClient
      .get<Account[]>('/accounts', {
        params: search.trim() ? { search: search.trim() } : undefined,
      })
      .then(({ data }) => setAccounts(data))
      .catch(() => setAccounts([]))
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <CreateAccountModal
        open={showCreateAccount}
        onClose={() => setShowCreateAccount(false)}
        onCreated={load}
      />
      <CreateContactModal
        open={!!contactForAccount}
        onClose={() => setContactForAccount(null)}
        onCreated={() => {}}
        preselectedAccountId={contactForAccount ?? undefined}
      />
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('nav.accounts')}</h1>
        <Button size="sm" onClick={() => setShowCreateAccount(true)}>
          + Account
        </Button>
      </div>
      <Input
        placeholder="Search accounts..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-sm"
      />
      {loading && (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      )}
      <ul className="divide-y rounded-lg border bg-card">
        {accounts.map((a) => (
          <li
            key={a.id}
            className="flex items-center justify-between px-4 py-3"
          >
            <div>
              <Link to={`/accounts/${a.id}`} className="font-medium hover:underline">
                {a.name}
              </Link>
              <div className="text-xs text-muted-foreground">
                {[a.industry, a.sizeBucket, a.website]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setContactForAccount(a.id)}
              >
                + Contact
              </Button>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                  a.status === 'ACTIVE'
                    ? 'bg-emerald-900/40 text-emerald-200'
                    : 'bg-muted text-foreground'
                }`}
              >
                {a.status}
              </span>
            </div>
          </li>
        ))}
        {!loading && accounts.length === 0 && (
          <li className="px-4 py-6 text-center text-sm text-muted-foreground">
            No accounts found
          </li>
        )}
      </ul>
    </div>
  );
}
